import { useId } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, ChevronDown, History, RefreshCw, ShieldCheck } from 'lucide-react';

import { useDismissibleDetails } from '../../../hooks/useDismissibleDetails';
import { cn } from '../../../lib/utils';
import { Button } from '../../ui/button';
import { buttonVariants } from '../../ui/button-variants';
import {
    menuItemClassName,
    menuItemDestructiveClassName,
    menuSeparatorClassName,
    popoverSurfaceClassName,
} from '../../ui/surface';
import { pageTitleClassName } from '../../ui/typography';
import { AdminActiveStatusBadge } from './AdminActiveStatusBadge';
import {
    ADMIN_BADGE_BASE_CLASS,
    ADMIN_BADGE_POSITIVE_CLASS,
    ADMIN_BADGE_WARNING_CLASS,
} from './adminStatusBadge';
import { AccessBadge } from './CompanyDetailPrimitives';
import type { CompanySsoDetailState } from './companySsoDetail';

export function CompanyDetailHeader({
    companyId,
    companyDisplayName,
    companyIsActive,
    companyHasProAccess,
    companyHasPulseAccess,
    companyLoaded,
    onBack,
    onEditCompany,
    isEditDisabled,
    ssoDetailState,
    teamsLoaded,
    isCompanySyncedToAuth0,
    isCompanyAuth0Syncing,
    canSyncCompanyUsers,
    onSyncCompany,
    onOpenHistoricalImport,
}: {
    companyId?: string;
    companyDisplayName: string;
    companyIsActive?: boolean;
    companyHasProAccess: boolean;
    companyHasPulseAccess: boolean;
    companyLoaded: boolean;
    onBack: () => void;
    onEditCompany: () => void;
    isEditDisabled: boolean;
    ssoDetailState: CompanySsoDetailState;
    teamsLoaded: boolean;
    isCompanySyncedToAuth0: boolean;
    isCompanyAuth0Syncing: boolean;
    canSyncCompanyUsers: boolean;
    onSyncCompany: (syncUsers: boolean) => void;
    onOpenHistoricalImport: () => void;
}) {
    const developerMenuRef = useDismissibleDetails();
    const developerSummaryId = useId();
    const manageSsoReasonId = useId();
    return (
        <div className="relative z-20 flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
            <div className="flex min-w-0 items-start gap-3">
                <Button variant="ghost" size="icon" aria-label="Back" onClick={onBack}>
                    <ArrowLeft className="size-5" aria-hidden />
                </Button>
                <div className="min-w-0">
                    <p className="text-sm text-neutral-500">Company</p>
                    <h1 className={pageTitleClassName}>{companyDisplayName}</h1>
                    <div className="mt-2 flex flex-wrap items-center gap-1.5">
                        <p className="text-sm text-neutral-500">ID: {companyId}</p>
                        {companyIsActive !== undefined && (
                            <AdminActiveStatusBadge isActive={companyIsActive} />
                        )}
                        <CompanyAccessBadges
                            loaded={companyLoaded}
                            hasProAccess={companyHasProAccess}
                            hasPulseAccess={companyHasPulseAccess}
                        />
                        {teamsLoaded &&
                            (isCompanySyncedToAuth0 ? (
                                <>
                                    <span
                                        className={cn(
                                            ADMIN_BADGE_BASE_CLASS,
                                            ADMIN_BADGE_POSITIVE_CLASS,
                                        )}
                                    >
                                        Auth0 synced
                                    </span>
                                    <span
                                        className={cn(
                                            ADMIN_BADGE_BASE_CLASS,
                                            ssoDetailState.badgeClassName,
                                        )}
                                    >
                                        {ssoDetailState.badgeLabel}
                                    </span>
                                </>
                            ) : (
                                <span
                                    className={cn(
                                        ADMIN_BADGE_BASE_CLASS,
                                        ADMIN_BADGE_WARNING_CLASS,
                                    )}
                                >
                                    Auth0 not synced
                                </span>
                            ))}
                    </div>
                </div>
            </div>
            <div className="flex shrink-0 flex-wrap items-center gap-2 pl-12 md:pl-0">
                <Button
                    type="button"
                    variant="outline"
                    onClick={onEditCompany}
                    disabled={isEditDisabled}
                >
                    Edit
                </Button>
                <details ref={developerMenuRef} className="relative z-30 shrink-0">
                    <summary
                        id={developerSummaryId}
                        className={cn(
                            buttonVariants({ variant: 'outline' }),
                            'cursor-pointer list-none [&::-webkit-details-marker]:hidden',
                        )}
                    >
                        Developer
                        <ChevronDown className="size-4 text-neutral-500" aria-hidden />
                    </summary>
                    <div
                        role="menu"
                        aria-labelledby={developerSummaryId}
                        className={cn(
                            popoverSurfaceClassName,
                            'absolute right-0 z-40 mt-2 flex min-w-56 flex-col p-2',
                        )}
                    >
                        <button
                            type="button"
                            role="menuitem"
                            className={menuItemClassName}
                            onClick={onOpenHistoricalImport}
                        >
                            <History className="size-4" aria-hidden />
                            Historical import…
                        </button>
                        {ssoDetailState.manageSsoDisabled || !ssoDetailState.manageSsoPath ? (
                            <button
                                type="button"
                                role="menuitem"
                                disabled
                                title={ssoDetailState.manageSsoTooltip ?? undefined}
                                aria-describedby={
                                    ssoDetailState.manageSsoTooltip ? manageSsoReasonId : undefined
                                }
                                className={menuItemClassName}
                            >
                                <ShieldCheck
                                    className="mt-0.5 size-4 shrink-0 self-start"
                                    aria-hidden
                                />
                                <span className="flex flex-col items-start">
                                    <span>Manage SSO</span>
                                    {ssoDetailState.manageSsoTooltip ? (
                                        <span
                                            id={manageSsoReasonId}
                                            className="text-xs font-normal text-neutral-500"
                                        >
                                            {ssoDetailState.manageSsoTooltip}
                                        </span>
                                    ) : null}
                                </span>
                            </button>
                        ) : (
                            <Link
                                to={ssoDetailState.manageSsoPath}
                                role="menuitem"
                                className={menuItemClassName}
                            >
                                <ShieldCheck className="size-4" aria-hidden />
                                Manage SSO
                            </Link>
                        )}
                        <CompanySyncMenuItems
                            visible={companyLoaded && !isCompanySyncedToAuth0}
                            isSyncing={isCompanyAuth0Syncing}
                            canSyncUsers={canSyncCompanyUsers}
                            onSyncCompany={onSyncCompany}
                        />
                    </div>
                </details>
            </div>
        </div>
    );
}

function CompanyAccessBadges({
    loaded,
    hasProAccess,
    hasPulseAccess,
}: {
    loaded: boolean;
    hasProAccess: boolean;
    hasPulseAccess: boolean;
}) {
    if (!loaded) return null;

    return (
        <>
            <AccessBadge label="Pro" hasAccess={hasProAccess} />
            <AccessBadge label="Pulse" hasAccess={hasPulseAccess} />
        </>
    );
}

function CompanySyncMenuItems({
    visible,
    isSyncing,
    canSyncUsers,
    onSyncCompany,
}: {
    visible: boolean;
    isSyncing: boolean;
    canSyncUsers: boolean;
    onSyncCompany: (syncUsers: boolean) => void;
}) {
    const syncUsersReasonId = useId();
    if (!visible) return null;

    return (
        <>
            <div role="separator" className={menuSeparatorClassName} />
            <button
                type="button"
                role="menuitem"
                aria-label="Org sync: sync company organization to Auth0"
                disabled={isSyncing}
                className={cn(menuItemClassName, menuItemDestructiveClassName)}
                onClick={() => onSyncCompany(false)}
            >
                <RefreshCw className={`size-4 ${isSyncing ? 'animate-spin' : ''}`} aria-hidden />
                Org sync
            </button>
            <button
                type="button"
                role="menuitem"
                aria-label="Org + users sync: sync company organization and users to Auth0"
                disabled={isSyncing || !canSyncUsers}
                title={canSyncUsers ? undefined : 'Requires administrate:users permission'}
                aria-describedby={!canSyncUsers ? syncUsersReasonId : undefined}
                className={cn(menuItemClassName, menuItemDestructiveClassName)}
                onClick={() => onSyncCompany(true)}
            >
                <RefreshCw
                    className={`mt-0.5 size-4 self-start ${isSyncing ? 'animate-spin' : ''}`}
                    aria-hidden
                />
                <span className="flex flex-col items-start">
                    <span>Org + users sync</span>
                    {!canSyncUsers ? (
                        <span
                            id={syncUsersReasonId}
                            className="text-xs font-normal text-neutral-500"
                        >
                            Requires administrate:users permission
                        </span>
                    ) : null}
                </span>
            </button>
        </>
    );
}
